import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LegacyAdminRedirectRoutes } from '@/components/routing/LegacyAdminRedirects'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ImpersonationProvider } from './contexts/ImpersonationContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { AccessibilityProvider } from './contexts/AccessibilityContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { RoleBasedRedirect } from './components/auth/RoleBasedRedirect'
import { DeferredRoute, StagingHiddenRoute, DedicatedAffiliateGuard } from './components/auth/DeferredRoute'
import { Layout } from './components/layout/Layout'
import { PublicLayout } from './components/layout/PublicLayout'
import { CookieNotification } from './components/legal/CookieNotification'
import { RouteLogger } from './components/logging/RouteLogger'
import { ErrorBoundary } from './components/logging/ErrorBoundary'
import { PasswordChangeWrapper } from './components/auth/PasswordChangeWrapper'
import { LoadingSpinner } from './components/ui/LoadingSpinner'
import { I18nProvider } from './contexts/I18nContext'
import { ShowTestDataProvider } from './contexts/ShowTestDataContext'
import { isEmbedPath } from './utils/url'

// Lazy load all route components for code splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })))
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })))
const AcceptInvitePage = lazy(() => import('./pages/auth/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })))
const CompleteAccountPage = lazy(() => import('./pages/auth/CompleteAccountPage').then(m => ({ default: m.CompleteAccountPage })))
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage').then(m => ({ default: m.OnboardingPage })))
const OAuthCallbackPage = lazy(() => import('./pages/auth/OAuthCallbackPage').then(m => ({ default: m.OAuthCallbackPage })))
const PendingApprovalPage = lazy(() => import('./pages/auth/PendingApprovalPage').then(m => ({ default: m.PendingApprovalPage })))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const HotelsPage = lazy(() => import('./pages/hotels/HotelsPage').then(m => ({ default: m.HotelsPage })))
const HotelPerformancePage = lazy(() => import('./pages/hotels/HotelPerformancePage').then(m => ({ default: m.HotelPerformancePage })))
const HotelPerformanceSalesPage = lazy(() => import('./pages/hotels/HotelPerformanceSalesPage').then(m => ({ default: m.HotelPerformanceSalesPage })))
const HotelReservationsPage = lazy(() => import('./pages/hotels/HotelReservationsPage').then(m => ({ default: m.HotelReservationsPage })))
const HotelVisibilityPage = lazy(() => import('./pages/hotels/HotelVisibilityPage').then(m => ({ default: m.HotelVisibilityPage })))
// #642 Section B — chain-aware branch for /hotel-affiliations/pending.
const HotelPendingAffiliationsRoute = lazy(() => import('./pages/hotels/HotelPendingAffiliationsRoute').then(m => ({ default: m.HotelPendingAffiliationsRoute })))
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })))
const UsersPage = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })))
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage').then(m => ({ default: m.SystemSettingsPage })))
const ImpersonationPage = lazy(() => import('./pages/admin/ImpersonationPage').then(m => ({ default: m.ImpersonationPage })))
// #533 Phase 2b — Form-settings tab extracted to its own route. The page is
// configuration (PMS / Booking-Engine vocab), not workflow, so it lives under
// /admin/settings/* alongside the rest of the system-settings surface.
const AdminLookupOptionsPage = lazy(() => import('./pages/admin/AdminLookupOptionsPage'))
const MediaEnrichmentPage = lazy(() => import('./pages/admin/MediaEnrichmentPage').then(m => ({ default: m.MediaEnrichmentPage })))
// #474 M0a — admin Hotel Geocoding tool. Backfills lat/lng for hotels
// missing coordinates so the Discovery v2 map workspace can render.
const HotelGeocodingPage = lazy(() => import('./pages/admin/HotelGeocodingPage').then(m => ({ default: m.HotelGeocodingPage })))
const PmsSyncHealthPage = lazy(() => import('./pages/admin/PmsSyncHealthPage').then(m => ({ default: m.PmsSyncHealthPage })))
const SiteHealthPage = lazy(() => import('./pages/admin/SiteHealthPage').then(m => ({ default: m.SiteHealthPage })))
const PmsDashboardPage = lazy(() => import('./pages/admin/PmsDashboardPage').then(m => ({ default: m.PmsDashboardPage })))
// #1660/#1661 (parent #1560): manual reservation overrides, under Admin → System.
const AdminReservationOverridesPage = lazy(() => import('./pages/admin/AdminReservationOverridesPage').then(m => ({ default: m.AdminReservationOverridesPage })))
const CommissionParityPage = lazy(() => import('./pages/admin/CommissionParityPage').then(m => ({ default: m.CommissionParityPage })))
const ExclusionRetroPage = lazy(() => import('./pages/admin/ExclusionRetroPage').then(m => ({ default: m.ExclusionRetroPage })))
const ReservationInspectorPage = lazy(() => import('./pages/admin/ReservationInspectorPage').then(m => ({ default: m.ReservationInspectorPage })))
const PmsVerificationPage = lazy(() => import('./pages/admin/PmsVerificationPage').then(m => ({ default: m.PmsVerificationPage })))
const AffiliatesPage = lazy(() => import('./pages/affiliates/AffiliatesPage').then(m => ({ default: m.AffiliatesPage })))
const AffiliateKeyInsightsPage = lazy(() => import('./pages/affiliates/AffiliateKeyInsightsPage').then(m => ({ default: m.AffiliateKeyInsightsPage })))
const AffiliatePerformanceSalesPage = lazy(() => import('./pages/affiliates/AffiliatePerformanceSalesPage').then(m => ({ default: m.AffiliatePerformanceSalesPage })))
const AffiliateVisibilityPage = lazy(() => import('./pages/affiliates/AffiliateVisibilityPage').then(m => ({ default: m.AffiliateVisibilityPage })))
const AffiliateReservationsPage = lazy(() => import('./pages/affiliates/AffiliateReservationsPage').then(m => ({ default: m.AffiliateReservationsPage })))
const AffiliateHotelManagementPage = lazy(() => import('./pages/affiliates/AffiliateHotelManagementPage').then(m => ({ default: m.AffiliateHotelManagementPage })))
const AffiliateDetailsPage = lazy(() => import('./pages/affiliates/AffiliateDetailsPage').then(m => ({ default: m.AffiliateDetailsPage })))
// MaterialsPage was retired in #423; its route redirects to /dashboard.
// #1596 (A2): the affiliate Earnings & Payouts page replaces the payouts page
// #423 retired.
const PayoutsPage = lazy(() => import('./pages/payouts/PayoutsPage').then(m => ({ default: m.PayoutsPage })))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const CommissionsPage = lazy(() => import('./pages/commissions/CommissionsPage').then(m => ({ default: m.CommissionsPage })))
// #437: hotel-side commissions & payouts page — "what do I owe and when".
const HotelCommissionsPage = lazy(() => import('./pages/commissions/HotelCommissionsPage').then(m => ({ default: m.HotelCommissionsPage })))
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const PublicProfileView = lazy(() => import('./components/profile/PublicProfileView'))
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
// #1054 — DPA referenced from clause 11.8 of the affiliate agreement.
const DpaPage = lazy(() => import('./pages/legal/DpaPage').then(m => ({ default: m.DpaPage })))
// #1054 — the sub-processor register DPA clause 5 sends partners to.
const SubProcessorsPage = lazy(() => import('./pages/legal/SubProcessorsPage').then(m => ({ default: m.SubProcessorsPage })))
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })))
const CookiePolicyPage = lazy(() => import('./pages/legal/CookiePolicyPage').then(m => ({ default: m.CookiePolicyPage })))
const WelcomePage = lazy(() => import('./pages/welcome/WelcomePage'))
// #signup-revamp Phase 2C — state-aware page for users in pending_* states
const OnboardingStatusPage = lazy(() => import('./pages/onboarding/OnboardingStatusPage'))
// #signup-revamp Phase 2E / #491 Slice 2 — the ops queue + PMS/Booking-Engine
// lookup editors are now the "Queue" and "Form settings" tabs inside
// OnboardingAdminPage (#528 S7), not routed directly here.
// #528 S7 — unified admin Onboarding shell (Queue + Submissions + Form settings)
const OnboardingAdminPage = lazy(() => import('./pages/admin/OnboardingAdminPage'))
// #signup-revamp Phase 3C — contract review + sign/decline
const OnboardingContractPage = lazy(() => import('./pages/onboarding/OnboardingContractPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const HotelOnboardingPage = lazy(() => import('./pages/onboarding/HotelOnboardingPage').then(m => ({ default: m.HotelOnboardingPage })))
const AffiliateOnboardingPage = lazy(() => import('./pages/onboarding/AffiliateOnboardingPage').then(m => ({ default: m.AffiliateOnboardingPage })))
// #1499 / #1646 — public affiliate-program info page (was the Base44 app).
const AffInfoPage = lazy(() => import('./pages/onboarding/AffInfoPage').then(m => ({ default: m.AffInfoPage })))
// #284 — hotel-embedded "Work With Us" widget + its top-level quick-apply target.
const EmbedWorkWithUsPage = lazy(() => import('./pages/embed/EmbedWorkWithUsPage').then(m => ({ default: m.EmbedWorkWithUsPage })))
const ApplyToHotelPage = lazy(() => import('./pages/embed/ApplyToHotelPage').then(m => ({ default: m.ApplyToHotelPage })))
const HotelDetailsPage = lazy(() => import('./pages/hotels/HotelDetailsPage').then(m => ({ default: m.HotelDetailsPage })))
// #284 — hotel-side builder for the embedded "Work With Us" page.
const HotelWorkWithUsPage = lazy(() => import('./pages/hotels/HotelWorkWithUsPage').then(m => ({ default: m.HotelWorkWithUsPage })))
const HotelMediaBundlesPage = lazy(() => import('./pages/hotels/HotelMediaBundlesPage').then(m => ({ default: m.HotelMediaBundlesPage })))
const HotelMediaBundleEditorPage = lazy(() => import('./pages/hotels/HotelMediaBundleEditorPage').then(m => ({ default: m.HotelMediaBundleEditorPage })))
const AffiliateHotelMediaBundlesPage = lazy(() => import('./pages/affiliates/AffiliateHotelMediaBundlesPage').then(m => ({ default: m.AffiliateHotelMediaBundlesPage })))
const AffiliateHotelMediaBundleDetailPage = lazy(() => import('./pages/affiliates/AffiliateHotelMediaBundleDetailPage').then(m => ({ default: m.AffiliateHotelMediaBundleDetailPage })))
const UserDetailPage = lazy(() => import('./pages/admin/UserDetailPage').then(m => ({ default: m.UserDetailPage })))
const HotelROIRankingPage = lazy(() => import('./pages/admin/HotelROIRankingPage').then(m => ({ default: m.HotelROIRankingPage })))
const AffiliateProfitabilityPage = lazy(() => import('./pages/admin/AffiliateProfitabilityPage').then(m => ({ default: m.AffiliateProfitabilityPage })))
const CancellationAnalyticsPage = lazy(() => import('./pages/admin/CancellationAnalyticsPage').then(m => ({ default: m.CancellationAnalyticsPage })))
const CohortAnalysisPage = lazy(() => import('./pages/admin/CohortAnalysisPage').then(m => ({ default: m.CohortAnalysisPage })))
const CampaignAttributionPage = lazy(() => import('./pages/admin/CampaignAttributionPage').then(m => ({ default: m.CampaignAttributionPage })))
const ConversionFunnelPage = lazy(() => import('./pages/admin/ConversionFunnelPage').then(m => ({ default: m.ConversionFunnelPage })))
const RevenueForecastPage = lazy(() => import('./pages/admin/RevenueForecastPage').then(m => ({ default: m.RevenueForecastPage })))
// #1089 — unified platform-wide admin reports (Admin Reports section).
const AdminSalesPerformancePage = lazy(() => import('./pages/admin/reports/AdminSalesPerformancePage').then(m => ({ default: m.AdminSalesPerformancePage })))
const AdminVisibilityPerformancePage = lazy(() => import('./pages/admin/reports/AdminVisibilityPerformancePage').then(m => ({ default: m.AdminVisibilityPerformancePage })))
const AdminReservationsPage = lazy(() => import('./pages/admin/reports/AdminReservationsPage').then(m => ({ default: m.AdminReservationsPage })))
const AdminReservationReviewPage = lazy(() => import('./pages/admin/reports/AdminReservationReviewPage').then(m => ({ default: m.AdminReservationReviewPage })))
const DiscoveryPage = lazy(() => import('./pages/affiliates/DiscoveryPage').then(m => ({ default: m.DiscoveryPage })))
// #280 PR3 — Marketplace (tender browse/apply + hotel create/review).
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage').then(m => ({ default: m.MarketplacePage })))
// ContractTemplatesPage + ContractsOverviewPage are now the "Templates" and
// "Pipeline" tabs inside ContractsAdminPage (#528 S8), not routed directly.
const ContractsListPage = lazy(() => import('./pages/contracts/ContractsListPage').then(m => ({ default: m.ContractsListPage })))
const ContractViewPage = lazy(() => import('./pages/contracts/ContractViewPage').then(m => ({ default: m.ContractViewPage })))
// #528 S8 — unified admin Contracts shell (Pipeline + All contracts + Templates)
const ContractsAdminPage = lazy(() => import('./pages/admin/ContractsAdminPage'))
const SystemHealthPage = lazy(() => import('./pages/admin/SystemHealthPage').then(m => ({ default: m.SystemHealthPage })))
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const EmailTemplatesPage = lazy(() => import('./pages/admin/EmailTemplatesPage').then(m => ({ default: m.EmailTemplatesPage })))
const EmailCampaignsPage = lazy(() => import('./pages/admin/EmailCampaignsPage').then(m => ({ default: m.EmailCampaignsPage })))
const EmailSuppressionsPage = lazy(() => import('./pages/admin/EmailSuppressionsPage').then(m => ({ default: m.EmailSuppressionsPage })))
const EmailDistributionListsPage = lazy(() => import('./pages/admin/EmailDistributionListsPage').then(m => ({ default: m.EmailDistributionListsPage })))
const EmailCampaignEditPage = lazy(() => import('./pages/admin/EmailCampaignEditPage').then(m => ({ default: m.EmailCampaignEditPage })))
const EmailTemplateEditPage = lazy(() => import('./pages/admin/EmailTemplateEditPage').then(m => ({ default: m.EmailTemplateEditPage })))
const MessageDeliveryPage = lazy(() => import('./pages/admin/MessageDeliveryPage').then(m => ({ default: m.MessageDeliveryPage })))
const WhatsAppInboxPage = lazy(() => import('./pages/admin/WhatsAppInboxPage').then(m => ({ default: m.WhatsAppInboxPage })))
// Data issues inbox (spec 2026-09-29 §5).
const DataIssuesPage = lazy(() => import('./pages/admin/DataIssuesPage').then(m => ({ default: m.DataIssuesPage })))
const DataIssueDetailPage = lazy(() => import('./pages/admin/DataIssueDetailPage').then(m => ({ default: m.DataIssueDetailPage })))
const EmailSettingsPage = lazy(() => import('./pages/admin/EmailSettingsPage').then(m => ({ default: m.EmailSettingsPage })))
const MessagingPage = lazy(() => import('./pages/messaging/MessagingPage').then(m => ({ default: m.MessagingPage })))
// S6 (messaging revamp): admin moderation queue page.
const ModerationQueuePage = lazy(() => import('./pages/admin/ModerationQueuePage').then(m => ({ default: m.ModerationQueuePage })))
const UgcSubmissionPage = lazy(() => import('./pages/ugc/UgcSubmissionPage').then(m => ({ default: m.UgcSubmissionPage })))
const MySubmissionsPage = lazy(() => import('./pages/ugc/MySubmissionsPage').then(m => ({ default: m.MySubmissionsPage })))
const ReviewQueuePage = lazy(() => import('./pages/ugc/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage })))
const ContentLibraryPage = lazy(() => import('./pages/ugc/ContentLibraryPage').then(m => ({ default: m.ContentLibraryPage })))
const BrandGuidelinesPage = lazy(() => import('./pages/ugc/BrandGuidelinesPage').then(m => ({ default: m.BrandGuidelinesPage })))
const HotelPromotionsPage = lazy(() => import('./pages/promotions/HotelPromotionsPage').then(m => ({ default: m.HotelPromotionsPage })))
const PromotionFeedPage = lazy(() => import('./pages/promotions/PromotionFeedPage').then(m => ({ default: m.PromotionFeedPage })))
const AdminPromotionsPage = lazy(() => import('./pages/promotions/AdminPromotionsPage').then(m => ({ default: m.AdminPromotionsPage })))
const HotelVerificationPage = lazy(() => import('./pages/verification/HotelVerificationPage').then(m => ({ default: m.HotelVerificationPage })))
const AffiliateVerificationPage = lazy(() => import('./pages/verification/AffiliateVerificationPage').then(m => ({ default: m.AffiliateVerificationPage })))
const AdminVerificationPage = lazy(() => import('./pages/admin/AdminVerificationPage').then(m => ({ default: m.AdminVerificationPage })))
// #1595: admin bank-account verification queue (payouts need a verified account).
const BankAccountVerificationPage = lazy(() => import('./pages/admin/BankAccountVerificationPage').then(m => ({ default: m.BankAccountVerificationPage })))
// #1764 (A3 of #921): admin payouts console — queue, lines, transitions, generate with preview, platform minimum.
const AdminPayoutsPage = lazy(() => import('./pages/admin/AdminPayoutsPage').then(m => ({ default: m.AdminPayoutsPage })))
// #1669 (#1420): admin list of every hotel-affiliate affiliation, hidden/ended included.
const AdminAffiliationsPage = lazy(() => import('./pages/admin/AdminAffiliationsPage').then(m => ({ default: m.AdminAffiliationsPage })))
// #443: sync-conflicts admin queue UI — resolve legacy-vs-local write conflicts.
const SyncConflictsPage = lazy(() => import('./pages/admin/SyncConflictsPage').then(m => ({ default: m.SyncConflictsPage })))
// #484.3: Hotel Chain details — chain-wide brief + images.
const HotelChainDetailsPage = lazy(() => import('./pages/hotels/HotelChainDetailsPage').then(m => ({ default: m.HotelChainDetailsPage })))

// Auth-aware home redirect. Logged-in users at "/" go to their
// dashboard (which RoleBasedRedirect further specializes by role);
// logged-out visitors land on the public /welcome marketing page.
// Previously "/" hard-redirected to /welcome for everyone, so a
// signed-in hotel/affiliate user landing on the bare domain got
// dumped into the marketing hero with a "Sign up your hotel" CTA.
const HomeRedirect = () => {
  const { user } = useAuth()
  return <Navigate to={user ? '/dashboard' : '/welcome'} replace />
}

// Redirect that keeps the query string — for retired paths whose ?params
// still mean something at the new location (e.g. /commissions/rules?hotelId=).
const SearchPreservingRedirect = ({ to }: { to: string }) => {
  const location = useLocation()
  return <Navigate to={to + location.search} replace />
}

// Component that renders routes
const AppContent = () => {
  // #284 — the embed surfaces render inside the hotel's <iframe>; a fixed cookie
  // banner there covers the terms checkbox and the Submit button.
  const inEmbed = isEmbedPath(useLocation().pathname)
  return (
    <>
      <RouteLogger />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/welcome" element={<WelcomePage />} />
        {/* #signup-revamp Phase 2C — state-aware page for users in pending_* states.
            Not protected — pending users land here after login when Phase 2F's
            requireActiveStatus middleware bounces them off product routes. */}
        <Route path="/onboarding-status" element={<OnboardingStatusPage />} />
        {/* #signup-revamp Phase 3C — contract review + sign */}
        <Route path="/onboarding-status/contract" element={<OnboardingContractPage />} />
        {/* #signup-revamp Phase 2E + #491 Slice 2 — moved into the
            protected admin block below so these inherit Layout (sidebar)
            and the admin role gate. Leaving them as public siblings
            rendered them edge-to-edge with no sidebar. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={
          <PublicLayout>
            <OnboardingPage />
          </PublicLayout>
        } />
        <Route path="/register" element={<Navigate to="/onboarding" replace />} />
        {/* #284 — hotel-embedded "Work With Us" (iframe, layout-free, no auth)
            and the top-level quick-apply target for existing affiliates.
            /apply is public on purpose: it redirects to /login itself carrying
            state.from, which ProtectedRoute's /welcome bounce would drop. */}
        <Route path="/embed/work-with-us/:token" element={<EmbedWorkWithUsPage />} />
        <Route path="/apply/:hotelId" element={<ApplyToHotelPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* #1208 — consumes the single-use token in the reset-password
            email link. Public route. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* #499 — consumes the JWT in the verification-email link and
            hands the user off to /welcome on success. Public route. */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* #1052 part C — PUBLIC unsubscribe landing from campaign mail footers.
            No auth guard: recipients may have no account, and an unsubscribe
            behind a login is not an unsubscribe. The ?token= is the authority. */}
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        {/* #528 Slice 4 — hotel-team invitation acceptance. Public route
            (outside Layout) consuming the ?token= from the invite email. */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* #528 R5 — deferred credential activation (#529). Public route
            (no auth guard) consuming the ?token= from the activation email;
            sets the user's sign-in password, then logs them in. */}
        <Route path="/auth/complete-account" element={<CompleteAccountPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route path="/privacy-policy" element={
          <PublicLayout>
            <PrivacyPolicyPage />
          </PublicLayout>
        } />
        {/* #1054 — public like the other legal pages: a partner must be able to
            read the DPA from their contract. Signed in, clause 1 shows their
            own name; signed out it falls back to the document's own wording. */}
        <Route path="/dpa" element={
          <PublicLayout>
            <DpaPage />
          </PublicLayout>
        } />
        {/* #1054 — DPA clause 5 sends partners here to see the register and to
            register an email for change notifications. Public: a transparency
            register behind a login would not satisfy the clause. */}
        <Route path="/subprocessors" element={
          <PublicLayout>
            <SubProcessorsPage />
          </PublicLayout>
        } />
        <Route path="/terms-of-service" element={
          <PublicLayout>
            <TermsOfServicePage />
          </PublicLayout>
        } />
        <Route path="/cookie-policy" element={
          <PublicLayout>
            <CookiePolicyPage />
          </PublicLayout>
        } />
        <Route path="/profile/:userId" element={
          <PublicLayout>
            <PublicProfileView />
          </PublicLayout>
        } />
        <Route path="/onboarding/hotel" element={
          <PublicLayout>
            <HotelOnboardingPage />
          </PublicLayout>
        } />
        <Route path="/onboarding/affiliate" element={
          <PublicLayout>
            <AffiliateOnboardingPage />
          </PublicLayout>
        } />
        {/* #1499 / #1646 — public, unauthenticated affiliate-program info page
            moved from hoteliate-in-reg.base44.app. Informational only: its
            CTA links to /onboarding/affiliate; no data is collected. */}
        <Route path="/affinfo" element={
          <PublicLayout>
            <AffInfoPage />
          </PublicLayout>
        } />
        
        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <PasswordChangeWrapper>
                <Layout>
                  <RoleBasedRedirect>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/hotels/*" element={<ProtectedRoute roles={['admin', 'global_admin']}><HotelsPage /></ProtectedRoute>} />
                    <Route path="/hotel-dashboard" element={<HotelPerformancePage />} />
                    <Route path="/hotel-performance" element={<HotelPerformanceSalesPage />} />
                    <Route path="/hotel-visibility" element={<HotelVisibilityPage />} />
                    <Route path="/hotel-reservations" element={<HotelReservationsPage />} />
                    {/* #876: all three affiliation routes go through the
                        chain-aware wrapper. It renders the consolidated chain
                        grid for multi-hotel chain users on ANY of these routes
                        (previously only /pending), and falls back to the flat
                        HotelActiveAffiliationsPage for admins, single-hotel, and
                        non-chain users — so their view is unchanged. */}
                    <Route path="/hotel-affiliations" element={<HotelPendingAffiliationsRoute />} />
                    <Route path="/hotel-affiliations/active" element={<HotelPendingAffiliationsRoute />} />
                    <Route path="/hotel-affiliations/pending" element={<HotelPendingAffiliationsRoute />} />
                    {/* Backward-compat: legacy hyphenated paths from before #423
                        consolidated active+pending into /hotel-affiliations.
                        Bookmarks and external links still point at these. */}
                    <Route path="/hotel-active-affiliations" element={<Navigate to="/hotel-affiliations/active" replace />} />
                    <Route path="/hotel-pending-affiliations" element={<Navigate to="/hotel-affiliations/pending" replace />} />
                    <Route path="/hotel-details" element={<HotelDetailsPage />} />
                    {/* #284 — embedded "Work With Us" builder. Admin-only until a
                        subscription gate exists: the hotel nav tree has no entry
                        and RoleBasedRedirect does not allow-list the path, so this
                        guard is what turns a typed URL into a /dashboard bounce. */}
                    <Route path="/hotel-work-with-us" element={<ProtectedRoute roles={['admin', 'global_admin']}><HotelWorkWithUsPage /></ProtectedRoute>} />
                    {/* #277 hotel-side media bundles */}
                    <Route path="/hotel-media-bundles" element={<HotelMediaBundlesPage />} />
                    <Route path="/hotel-media-bundles/:hotelId/:bundleId" element={<HotelMediaBundleEditorPage />} />
                    {/* #277 affiliate-side bundle browse + download */}
                    <Route path="/affiliate-hotels/:hotelId/media-bundles" element={<AffiliateHotelMediaBundlesPage />} />
                    <Route path="/affiliate-hotels/:hotelId/media-bundles/:bundleId" element={<AffiliateHotelMediaBundleDetailPage />} />
                    {/* #484.3 hotel chain details */}
                    <Route path="/chain-details" element={<HotelChainDetailsPage />} />
                    <Route path="/affiliates/*" element={<ProtectedRoute roles={['admin', 'global_admin']}><AffiliatesPage /></ProtectedRoute>} />
                    <Route path="/affiliate-dashboard" element={<AffiliateKeyInsightsPage />} />
                    <Route path="/affiliate-performance" element={<AffiliatePerformanceSalesPage />} />
                    <Route path="/affiliate-visibility" element={<AffiliateVisibilityPage />} />
                    <Route path="/affiliate-reservations" element={<AffiliateReservationsPage />} />
                    <Route path="/affiliate-hotels" element={<AffiliateHotelManagementPage />} />
                    <Route path="/affiliate-details" element={<AffiliateDetailsPage />} />
                    {/* /materials was retired in #423 (see navigationSections.ts
                        header) and redirects to /dashboard for everybody, the
                        retired-page pattern used for /admin/legacy-import. */}
                    <Route path="/materials/*" element={<Navigate to="/dashboard" replace />} />
                    {/* Retired: /commission-rules rendered a page that read
                        :hotelId from useParams, but its route never supplied
                        one — so the list query stayed disabled and the screen
                        was permanently empty. /admin/commissions/rules is the
                        working equivalent (and is admin-gated there). */}
                    <Route path="/commission-rules/*" element={<SearchPreservingRedirect to="/admin/commissions/rules" />} />
                    {/* #1596 (A2): affiliate Earnings & Payouts. The payout API is
                        scoped to req.user.affiliateId, so an admin session (which
                        has none) gets a notice on the page instead of requests
                        the API would refuse; the nav entry is affiliate-only. */}
                    <Route path="/payouts" element={<PayoutsPage />} />
                    {/* Admin commission surfaces moved under /admin/* so the
                        System nav group lives in one URL family — the old
                        /commissions/rules|simulator paths redirect (they were
                        admin-only anyway). #2071: /commissions and
                        /commissions/history are admin-only too — the
                        affiliate Commission History is hidden from
                        affiliates, so no partner reaches this family. */}
                    {/* Single-line on purpose: check-route-allowlist.js only
                        sees a route whose path sits on the <Route line, and it
                        must see these to verify the admin wrap. */}
                    <Route path="/commissions/rules" element={<ProtectedRoute roles={['admin', 'global_admin']}><SearchPreservingRedirect to="/admin/commissions/rules" /></ProtectedRoute>} />
                    <Route path="/commissions/simulator" element={<ProtectedRoute roles={['admin', 'global_admin']}><SearchPreservingRedirect to="/admin/commissions/simulator" /></ProtectedRoute>} />
                    <Route path="/admin/commissions/*" element={<CommissionsPage />} />
                    <Route path="/commissions/*" element={<ProtectedRoute roles={['admin', 'global_admin']}><CommissionsPage /></ProtectedRoute>} />
                    {/* #437: hotel-side commissions/payouts view. #2071: hidden
                        from hotel users, so admin-only (the hotel nav tree has
                        no entry and RoleBasedRedirect does not list it). Kept on
                        one line so check-route-allowlist.js sees the route. */}
                    <Route path="/hotel-commissions" element={<ProtectedRoute roles={['admin', 'global_admin']}><HotelCommissionsPage /></ProtectedRoute>} />
                    {/* #2071: Notifications is hidden from every partner user
                        (hotel, chain hotel, affiliate), so admin-only. Single-line
                        so check-route-allowlist.js sees the admin wrap. */}
                    <Route path="/notifications/*" element={<ProtectedRoute roles={['admin', 'global_admin']}><NotificationsPage /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/settings/*" element={<SettingsPage />} />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <UsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users/:id"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <UserDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* /admin/roles redirects to /admin/settings (roles tab is now under settings) */}
                    <Route path="/admin/roles" element={<Navigate to="/admin/settings" replace />} />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <SystemSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #533 Phase 2b — Onboarding form options moved out of
                        the /admin/onboarding tab shell. The IA review (rework
                        plan finding #9) flagged Form settings as configuration
                        belonging next to the rest of /admin/settings/*, not
                        in the workflow-oriented Onboarding tabs. */}
                    <Route
                      path="/admin/settings/onboarding-form"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminLookupOptionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/impersonation"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ImpersonationPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #528 S7 — unified admin Onboarding shell (3 → 1).
                        Queue / Submissions / Form settings live as
                        URL-addressable tabs (?tab=queue|submissions|settings,
                        default queue). The legacy /admin/onboarding-queue and
                        /admin/onboarding-lookups paths redirect onto the
                        matching tab. */}
                    <Route
                      path="/admin/onboarding"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <OnboardingAdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/onboarding-queue"
                      element={<Navigate to="/admin/onboarding?tab=queue" replace />}
                    />
                    {/* #533 Phase 2b — legacy /admin/onboarding-lookups now
                        redirects straight to the new /admin/settings/onboarding-form
                        route (skipping the intermediate ?tab=settings hop that
                        the tab-shell shim would otherwise handle). */}
                    <Route
                      path="/admin/onboarding-lookups"
                      element={<Navigate to="/admin/settings/onboarding-form" replace />}
                    />
                    <Route path="/admin/legacy-import" element={<Navigate to="/admin/settings" replace />} />
                    <Route
                      path="/admin/media-enrichment"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <MediaEnrichmentPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #474 M0a — admin-triggered Hotel Geocoding tool.
                        Backfills lat/lng/google_place_id for hotels missing
                        coordinates so Discovery v2 map workspace can render. */}
                    <Route
                      path="/admin/tools/hotel-geocoding"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <HotelGeocodingPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1412 Part 2 — PMS Dashboard: provider-level integration health under System. */}
                    <Route
                      path="/admin/system/pms-dashboard"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <PmsDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1560 — manual reservation overrides (admin only, by URL and by API). */}
                    <Route
                      path="/admin/system/reservation-overrides"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminReservationOverridesPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1058 — PMS Sync Health: per-hotel Optima poll reachability for ops. */}
                    <Route
                      path="/admin/tools/pms-sync-health"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <PmsSyncHealthPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1549/#1627 — Site Health: daily tracking-plugin probe of every hotel site. */}
                    <Route
                      path="/admin/tools/site-health"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <SiteHealthPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy-migration money gate: parity-harness runs vs the ≥99.5% cutover bar. */}
                    <Route
                      path="/admin/tools/commission-parity"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <CommissionParityPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #2236 — commissions earning inside a live exclusion: admin applies the retro zero / PAID clawback. */}
                    <Route
                      path="/admin/tools/exclusion-retro"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ExclusionRetroPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1021 — standing PMS-amount adjudication: disputes, sweep trigger, verdicts. */}
                    <Route
                      path="/admin/tools/pms-verification"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <PmsVerificationPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Cross-source reservation drill-down: legacy vs PMS vs platform. */}
                    <Route
                      path="/admin/tools/reservation-inspector"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ReservationInspectorPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin Report Routes - Hotels
                     *
                     * The per-hotel and per-affiliate report views (key-insights,
                     * performance, visibility, reservations) used to be duplicated
                     * here under /admin/reports/*. Those eight routes rendered the
                     * exact same components as the /hotel-* and /affiliate-* paths
                     * the admin menu already links to, had zero inbound links, and
                     * were removed — one view, one URL. Admins reach them through
                     * the same paths as tenants; scoping is handled inside the
                     * pages, not by the route. Only the views with no tenant-side
                     * equivalent (roi-ranking, cancellation-analytics) live here.
                     */}
                    <Route
                      path="/admin/reports/hotels/roi-ranking"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <HotelROIRankingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/affiliates/profitability"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AffiliateProfitabilityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/cancellation-analytics"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <CancellationAnalyticsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/affiliate-cohorts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <CohortAnalysisPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/campaign-attribution"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <CampaignAttributionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/conversion-funnel"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ConversionFunnelPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/revenue-forecast"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <RevenueForecastPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1089 — unified platform-wide reports. Unlike the eight
                        routes removed above, these have NO tenant-side
                        equivalent: each aggregates across every hotel AND every
                        affiliate at once, behind one entity toggle. */}
                    <Route
                      path="/admin/reports/performance-sales"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminSalesPerformancePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/performance-visibility"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminVisibilityPerformancePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports/reservations"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminReservationsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1655 (#1299) — triage + bulk internal status. Admin only:
                        it lists the rows hidden from hotels and affiliates. */}
                    <Route
                      path="/admin/reports/reservation-review"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminReservationReviewPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Contract Routes */}
                    {/* #528 S8 — unified admin Contracts shell (3 → 1).
                        Pipeline / All contracts / Templates as URL-addressable
                        tabs (?tab=pipeline|all|templates, default pipeline).
                        /admin/contract-templates redirects onto the Templates
                        tab. Partner-facing /contracts + /contracts/:id stay
                        unchanged below. */}
                    <Route
                      path="/admin/contracts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ContractsAdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/contract-templates"
                      element={<Navigate to="/admin/contracts?tab=templates" replace />}
                    />
                    {/* #1669 (#1420): Admin Affiliations Management — every
                        affiliation row, hide/Ended/history included. Read-only
                        for now; the actions land in #1664/#1665/#1666. */}
                    <Route
                      path="/admin/affiliations"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminAffiliationsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #2071: the contracts LIST is admin-only (hidden from
                        affiliates; hotel users lost it in #968). The single
                        contract stays reachable for its parties: the "awaiting
                        your signature" links land there. Single-line so
                        check-route-allowlist.js sees the admin wrap. */}
                    <Route path="/contracts" element={<ProtectedRoute roles={['admin', 'global_admin']}><ContractsListPage /></ProtectedRoute>} />
                    <Route path="/contracts/:id" element={<StagingHiddenRoute><ContractViewPage /></StagingHiddenRoute>} />
                    {/* Control Mechanism Routes */}
                    <Route
                      path="/admin/system-health"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <SystemHealthPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/audit-log"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AuditLogPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* S6 (messaging revamp): admin moderation queue. */}
                    <Route
                      path="/admin/messaging/moderation"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <ModerationQueuePage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Communication (Email + WhatsApp) management routes — #1331 Part 1c renamed
                        the Email pages; the old URLs redirect (LegacyAdminRedirects). */}
                    {LegacyAdminRedirectRoutes()}
                    <Route
                      path="/admin/communication-templates"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailTemplatesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communication-templates/:slug"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailTemplateEditPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1695 — campaigns split into two pages by kind: Automations
                        (event-triggered) and Broadcasts (one-time). The old
                        /admin/communication-campaigns URLs redirect to Broadcasts
                        (LegacyAdminRedirects); the editor moves an automation on.
                        key={kind} keeps a draft typed on one page off the other. */}
                    <Route
                      path="/admin/communication-automations"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailCampaignsPage key="automation" kind="automation" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communication-automations/:id"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailCampaignEditPage key="automation" kind="automation" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communication-broadcasts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailCampaignsPage key="one_shot" kind="one_shot" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communication-broadcasts/:id"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailCampaignEditPage key="one_shot" kind="one_shot" />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1052 part E2 — saved audiences. */}
                    <Route
                      path="/admin/email-distribution-lists"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailDistributionListsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1052 part C — the never-email suppression list. */}
                    <Route
                      path="/admin/communication-suppressions"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailSuppressionsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* /admin/email-logs was the partner-service email-provider
                        log view; /admin/message-delivery (notification outbox,
                        all channels incl. email) is the canonical surface —
                        redirect the old URL so existing links still work. */}
                    <Route path="/admin/email-logs" element={<SearchPreservingRedirect to="/admin/message-delivery" />} />
                    <Route
                      path="/admin/message-delivery"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <MessageDeliveryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communication-inbox"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <WhatsAppInboxPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Data issues inbox (spec 2026-09-29 §5): literal paths, so the nav drift test resolves them. */}
                    <Route
                      path="/admin/data-issues"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <DataIssuesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/data-issues/:issueId"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <DataIssueDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/email-settings"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <EmailSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Promotion Routes — #650: hidden before the prod cut. */}
                    <Route path="/hotel-promotions" element={<DeferredRoute><HotelPromotionsPage /></DeferredRoute>} />
                    <Route path="/promotions" element={<DeferredRoute><PromotionFeedPage /></DeferredRoute>} />
                    {/* Discovery — sidebar links here on both hotel and affiliate
                        sides. #1210: dedicated-only affiliate users are redirected
                        to the dashboard (real admins bypass). */}
                    <Route path="/discovery" element={<DedicatedAffiliateGuard><DiscoveryPage /></DedicatedAffiliateGuard>} />
                    {/* #280 PR3 — Marketplace. Affiliates browse/apply to open
                        tenders; hotel users create tenders + review applications;
                        admins see both. */}
                    {/* #650: marketplace hidden before the prod cut. */}
                    <Route path="/marketplace" element={<DeferredRoute><MarketplacePage /></DeferredRoute>} />
                    <Route
                      path="/admin/promotions"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminPromotionsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Verification Routes — #650: hidden before the prod cut. */}
                    <Route path="/hotel/verification" element={<DeferredRoute><HotelVerificationPage /></DeferredRoute>} />
                    <Route path="/affiliate/verification" element={<DeferredRoute><AffiliateVerificationPage /></DeferredRoute>} />
                    <Route
                      path="/admin/verification"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminVerificationPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1595: approve/reject affiliate bank accounts. */}
                    <Route
                      path="/admin/bank-accounts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <BankAccountVerificationPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #1764: admin payouts console. */}
                    <Route
                      path="/admin/payouts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <AdminPayoutsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* #443: admin sync-conflicts resolution queue */}
                    <Route
                      path="/admin/sync-conflicts"
                      element={
                        <ProtectedRoute roles={['admin', 'global_admin']}>
                          <SyncConflictsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Messaging Routes — #650: hidden before the prod cut. */}
                    <Route path="/messaging" element={<DeferredRoute><MessagingPage /></DeferredRoute>} />
                    {/* UGC Routes — #650: hidden before the prod cut. All of
                        them go through DeferredRoute like my-submissions: the
                        submit flow ended on an admin-only page, and the
                        hotel-scoped pages rendered blank for affiliates
                        (PUX-10). Admins keep QA access until ship-status is
                        decided. */}
                    <Route path="/ugc/new" element={<DeferredRoute><UgcSubmissionPage /></DeferredRoute>} />
                    <Route path="/ugc/my-submissions" element={<DeferredRoute><MySubmissionsPage /></DeferredRoute>} />
                    <Route path="/ugc/submissions/:id" element={<DeferredRoute><MySubmissionsPage /></DeferredRoute>} />
                    <Route path="/ugc/review-queue" element={<DeferredRoute><ReviewQueuePage /></DeferredRoute>} />
                    <Route path="/ugc/content-library" element={<DeferredRoute><ContentLibraryPage /></DeferredRoute>} />
                    <Route path="/ugc/brand-guidelines" element={<DeferredRoute><BrandGuidelinesPage /></DeferredRoute>} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </RoleBasedRedirect>
                </Layout>
              </PasswordChangeWrapper>
            </ProtectedRoute>
          }
        />
      </Routes>
      
        {/* Cookie notification - appears on all pages EXCEPT the embed iframe (#284) */}
        {!inEmbed && <CookieNotification />}
      </Suspense>
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <AuthProvider>
          <I18nProvider>
            <ImpersonationProvider>
              <ShowTestDataProvider>
                <WebSocketProvider>
                  <AppContent />
                </WebSocketProvider>
              </ShowTestDataProvider>
            </ImpersonationProvider>
          </I18nProvider>
        </AuthProvider>
      </AccessibilityProvider>
    </ErrorBoundary>
  )
}

export default App